import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  Box, 
  Typography, 
  CircularProgress, 
  Tooltip, 
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { fetchLegMembersByCrewCode } from 'state/api';
import { filterCrewMember } from 'state/helpers';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import DataGridComponent from 'components/DataGridComponent';
import Header from 'components/Header';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import FilterButton from 'components/FilterButton';
import CrewFilterDialog from 'components/CrewFilterDialog';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';
import LegMembersDialog from 'scenes/inflightSales/LegMembersDialog';


const CrewList = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user); 
  const crewRanksData = useSelector((state) => state.crewRanks);
  const { startDate, endDate } = useDateRangeContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
  const canView   = hasRight(permissionMap, "CrewList", "view", false);
  const canEdit   = hasRight(permissionMap, "CrewList", "update", false);
  const canDelete = hasRight(permissionMap, "CrewList", "delete", false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState({ isActive: 'ALL' });
    const defaultFilters = {
      isActive: 'ALL',
    };
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [openLegMembersDialog, setOpenLegMembersDialog] = useState(false);
  
  const [deleteType, setDeleteType] = useState(null);
  const [crewMemberIdToDelete, setCrewMemberIdToDelete] = useState(null);

  const [selectedCrewCode, setSelectedCrewCode] = useState(null);
  const [selectedCrewName, setSelectedCrewName] = useState(null);
  const [crewRosters, setCrewRosters] = useState([]);
  const [crewMembers, setCrewMembers] = useState([]);


  
  useEffect(() => {
    const handleCrewList = async () => {
      if (!startDate || !endDate) return [];

      try {
        setLoading(true);
  
        const url = '/inflightsales/crewlist';
        const payload = { startDate, endDate };        
        const res = await api.post(url, payload);
        
        setCrewMembers(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    handleCrewList();
  }, [startDate, endDate]); 
  
  const filteredCrewMembers = useMemo(() => {
    if (!Array.isArray(crewMembers)) {
      return []; 
    }
    
    if (!filters) return crewMembers; 

    return crewMembers.filter(member => filterCrewMember(member, filters));
  }, [crewMembers, filters]);  

  const handleFilterOpen = () => setIsFilterDialogOpen(true);
  const handleFilterClose = () => setIsFilterDialogOpen(false);
  
  const handleFilterApply = (filterValues) => {
    setFilters(filterValues); 
  };

  const handleViewClick = useCallback(async (crewCode) => {
    if (!startDate || !endDate) return [];

    try {
      const fromDate = startDate;
      const toDate = endDate && new Date(endDate) > new Date() ? new Date() : endDate;
      
      const result = await fetchLegMembersByCrewCode(crewCode, fromDate, toDate);
      
      setCrewRosters(result);
      setOpenLegMembersDialog(true);
    } catch (error) {
      setOpenLegMembersDialog(false);
    }
  }, [startDate, endDate]);

  const handleDeleteClick = (crewMemberId) => {
    setCrewMemberIdToDelete(crewMemberId);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!crewMemberIdToDelete) return;
        
        await api.delete(`/crewmembers/delete/${crewMemberIdToDelete}`);

        setCrewMembers(crewMembers.filter((item) => item._id !== crewMemberIdToDelete));
        toast.success('Crew member deleted successfully.');
      } 
    } catch (error) {
      toast.error(error?.message || "Failed to delete crew member!");
    }
  }, [canDelete, deleteType, crewMemberIdToDelete, crewMembers]);
   
  const handleCloseLegMembersDialog = () => {
    setOpenLegMembersDialog(false);
    setCrewRosters([]);
  };
    
  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,      
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    },    
    {
      field: "crewCode",
      headerName: "Code",
      ...(() => {
        return isMobile ? { width: 60 } : { flex: 1 };
      })(),
    },    
    {
      field: "crewName",
      headerName: "CrewName",      
      cellClassName: "name-column--cell",
      ...(() => {
        return isMobile ? { width: 300 } : { flex: 4 };
      })(),
    },
    {
      field: "post",
      headerName: "Post",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1.2 };
      })(),
    },
    {
      field: "base",
      headerName: "Base",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1.2 };
      })(),
    },    
    {
      field: "trainingQuals",
      headerName: "Trainer",
      ...(() => {
        return isMobile ? { width: 80 } : { flex: 1.2 };
      })(),
    },
    {
      field: "quals",
      headerName: "Quals",
      ...(() => {
        return isMobile ? { width: 150 } : { flex: 2.5 };
      })(),
    },
  ], [isMobile]);

  const actionColumn = useMemo(() => {
    const activeCount = [canView, canEdit, canDelete].filter(Boolean).length;

    if (activeCount === 0) return [];
    const actionWidth = 65 + activeCount * 20;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,      
        sortable: false,
        width: actionWidth,      
        renderCell: (params) => {        
          return (          
            <FlexCenter
              gap={1}
              sx={{ width: '100%', height: '100%' }}
            >
              {canView && (
                <Tooltip title="View">
                  <IconButton
                    aria-label="View"
                    sx={{ color: "primary.dark" }}
                    onClick={() => {
                      setSelectedCrewCode(params.row.crewCode);
                      setSelectedCrewName(params.row.crewName);
                      handleViewClick(params.row.crewCode);
                    }}
                  >
                    <VisibilityIcon />
                  </IconButton>
                </Tooltip>
              )}
              {canEdit && (
                <Tooltip title="Edit">
                  <IconButton
                    aria-label="Edit"
                    sx={{ color: "primary.dark" }}
                    onClick={() => {
                      navigate(`/crewlist/${params.row.crewCode}`);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete">
                  <IconButton
                    aria-label="Delete"
                    sx={{ color: "primary.dark" }}
                    onClick={() => {
                      handleDeleteClick(params.row._id);
                    }}
                  >
                    <DeleteForeverIcon />
                  </IconButton>
                </Tooltip>
              )} 
            </FlexCenter>
          );
        },
      },
    ]
  }, [
    navigate,
    canView,
    canEdit,
    canDelete,
    handleViewClick, 
  ]); 

  const hiddenColumns = {
    _id: false,
    // post: false,
    // base: false,
  };

  const nonMobileHiddenColumns = {
    _id: false,
  };

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    isDesktop ? nonMobileHiddenColumns : hiddenColumns
  );

  const columnsToShow = [...columns, ...actionColumn];

  
  const crewRoutes = useMemo(() => {
    const groupAndSortLegs = (legs) => {
      if (!legs) return {}; 

      const groupedLegs = legs.reduce((acc, leg, index) => {
        const key = leg.routeNo ? `${leg.routeDate}-${leg.routeNo}` : `${leg.flightDate}-${leg.etd}-${index}`;
        if (!acc[key]) acc[key] = [];
        acc[key].push(leg);
        return acc;
      }, {});

      const sortedLegs = Object.keys(groupedLegs)
        .sort((a, b) => a.localeCompare(b)) 
        .reduce((sortedAcc, key) => {
          sortedAcc[key] = groupedLegs[key]; 
          return sortedAcc;
        }, {});

      return sortedLegs;
    };
    
    return groupAndSortLegs(crewRosters);
  }, [crewRosters])

  const {
    // genderOptions,
    baseOptions,
    postOptions,
    trainingQualOptions,
    // isActiveOptions
  } = useMemo(() => {
    if (!crewMembers?.length) {
      return {
        genderOptions: [],
        baseOptions: [],
        postOptions: [],
        trainingQualOptions: [],
        isActiveOptions: []
      };
    }

    const genders = new Set();
    const bases = new Set();
    const ranks = new Set();
    const trainingQuals = new Set();
    const isActives = new Set();

    for (const m of crewMembers) {
      if (m?.gender) genders.add(m.gender);
      if (m?.base) bases.add(m.base);
      if (m?.post) ranks.add(m.post);
      if (typeof m?.isActive === 'boolean') isActives.add(m.isActive);

      m?.trainingQuals?.forEach(q => q && trainingQuals.add(q));
    }

    const rankMap = new Map(
      crewRanksData.map(r => [r.rankCode, r])
    );

    const sortedRanks = [...ranks]
      .map(r => rankMap.get(r))
      .filter(Boolean)
      .sort((a, b) => a.rankPriority - b.rankPriority)
      .map(r => r.rankCode);

    return {
      genderOptions: [...genders].sort(),
      baseOptions: [...bases].sort(),
      postOptions: sortedRanks,
      trainingQualOptions: [...trainingQuals].sort(),
      isActiveOptions: [...isActives].sort()
    };
  }, [crewMembers, crewRanksData]);
  

  return (
    <Box className="main-container-box">        
      <FlexBetween>
        <Header 
          title="Crew List" 
          subtitle={`${loading ? 'Loading...' : 'List of Cabin Crew'}`}
        />          
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>            
            <FilterButton
              filters={filters}
              defaultFilters={defaultFilters}
              onClick={handleFilterOpen}
            />
          </Box>
        )}                
      </FlexBetween>
      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">
            Error: {error?.message || ''}
          </Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        >
          <Box 
            height={LAYOUT_HEIGHT} 
            sx={{ gridColumn: "span 12" }}
          >
            {isMobile ? (
              <CardList
                rows={filteredCrewMembers}
                primaryField="crewName"
                secondaryField="crewCode"
                fields={[
                  { label: "Post", key: "post" },
                  { label: "Base", key: "base" },
                  { label: "Trainer", key: "trainingQuals" },
                  { label: "Quals", key: "quals" },
                ]}
                isActiveField={null}
                canView={canView}
                canEdit={canEdit}
                canDelete={canDelete}
                onView={(id) => {
                  const m = filteredCrewMembers.find(x => x._id === id);
                  if (m) {
                    setSelectedCrewCode(m.crewCode);
                    setSelectedCrewName(m.crewName);
                    handleViewClick(m.crewCode);
                  }
                }}
                onEdit={(id) => {
                  const m = filteredCrewMembers.find(x => x._id === id);
                  if (m) navigate(`/crewlist/${m.crewCode}`);
                }}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={filteredCrewMembers || []}
                columns={columnsToShow}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  getRowId: (row) => row.crewCode || row._id,
                  initialState: {
                    pagination: { paginationModel: { pageSize: 100 } },
                  },
                }}
              />
            )}
          </Box>
        </Box>
      )}
      
      {confirmDialogOpen &&
        <DeleteConfirm
          open={confirmDialogOpen}
          onClose={() => setConfirmDialogOpen(false)}
          onConfirm={executeDelete}
          deleteType={deleteType}
        />
      }
      
      {isFilterDialogOpen &&
        <CrewFilterDialog
          open={isFilterDialogOpen}
          onClose={handleFilterClose}
          onApply={handleFilterApply}
          postOptions={postOptions}
          baseOptions={baseOptions}
          trainingQualOptions={trainingQualOptions}
          currentFilters={filters}
        />
      }

      {openLegMembersDialog &&
        <LegMembersDialog 
          open={openLegMembersDialog}
          onClose={handleCloseLegMembersDialog}
          crewCode={selectedCrewCode}
          crewName={selectedCrewName}
          crewLegs={crewRoutes}
        />
      }
    </Box>
  );
};

export default CrewList;
